/**
 * PulizieRistorazione — pagina Pulizie per variant "ristorazione" (R52.5)
 *
 * Stesso pattern di TemperatureRistorazione:
 *  - Header navy
 *  - Card "Da pulire oggi: X aree" + bottone "Inizia pulizie" (wizard one-by-one)
 *  - Search + chip Oggi/Settimana/Mese/Tutto + toggle Card/Tabella
 *  - Storico card compatte espandibili
 *  - Export CSV
 *
 * Modal wizard:
 *  - Header navy "Area X di Y · {Cucina}"
 *  - Toggle "Eseguita" / "Saltata"
 *  - Se "Saltata": dropdown motivo (Mancanza prodotti / Area inaccessibile / Locale chiuso / Altro)
 *  - Nota (obbligatoria solo per motivo "Altro")
 *  - Bottoni: Salta · Salva (next/close)
 *  - Operatore: nome utente loggato (no firma).
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { safePost } from "@/lib/safePost";
import { fetchTurni, findCurrentTurnoKey } from "@/lib/turni";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { apiErrMsg } from "@/lib/apiErr";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  SprayCan, Search, Download, ArrowRight, X, ChevronDown, ChevronUp,
  CheckCircle2, XCircle, BarChart3, LayoutGrid, Layers, Pencil, Settings2,
} from "lucide-react";
import { QuickEditDialog } from "@/components/QuickEditDialog";
import RegistroDetailDialog from "@/components/RegistroDetailDialog";

const MOTIVI = [
  { v: "mancanza_prodotti",  l: "Mancanza prodotti",  valid: false },
  { v: "area_inaccessibile", l: "Area inaccessibile", valid: true  },
  { v: "locale_chiuso",      l: "Locale chiuso",      valid: true  },
  { v: "altro",              l: "Altro",              valid: false },
];

const PERIODI = [
  { v: "oggi", l: "Oggi" },
  { v: "settimana", l: "Settimana" },
  { v: "mese", l: "Mese" },
  { v: "tutto", l: "Tutto" },
];

function startOfPeriod(periodo) {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  if (periodo === "settimana") d.setDate(d.getDate() - 7);
  else if (periodo === "mese") d.setDate(d.getDate() - 30);
  else if (periodo === "tutto") return null;
  // R53.15: fuso locale, non UTC
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const yest = new Date(today); yest.setDate(today.getDate() - 1);
    const dDate = new Date(d); dDate.setHours(0, 0, 0, 0);
    if (dDate.getTime() === today.getTime()) return "Oggi";
    if (dDate.getTime() === yest.getTime()) return "Ieri";
    return d.toLocaleDateString("it-IT", { day: "numeric", month: "short" });
  } catch { return iso; }
}

const DAY_LABELS = ["Lu", "Ma", "Me", "Gi", "Ve", "Sa", "Do"];
const MONTHS_IT = ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"];

// Lun=0 ... Dom=6 (locale Italian)
function dayOfWeekItIdx(d) { return (d.getDay() + 6) % 7; }
// R53.15: ymd in fuso LOCALE (vedi commento in TemperatureRistorazione.jsx).
function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function startOfWeekIt(d = new Date()) {
  const x = new Date(d); x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - dayOfWeekItIdx(x));
  return x;
}
function startOfMonth(d = new Date()) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonth(d = new Date()) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}

function colorClassFromPct(pct, hasExpected) {
  if (!hasExpected) return { cell: "bg-slate-200 text-slate-400", dot: "bg-slate-300", text: "text-slate-400" };
  if (pct >= 100) return { cell: "bg-emerald-500 text-white", dot: "bg-emerald-500", text: "text-emerald-600" };
  if (pct >= 50)  return { cell: "bg-amber-500 text-white",   dot: "bg-amber-500",   text: "text-amber-600" };
  return                  { cell: "bg-rose-500 text-white",    dot: "bg-rose-500",    text: "text-rose-600" };
}

export default function PulizieRistorazione() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [items, setItems] = useState([]);
  const [todoData, setTodoData] = useState({ tasks: [], today: [], upcoming: [], laterToday: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [periodo, setPeriodo] = useState("oggi");
  const [expandedId, setExpandedId] = useState(null);
  // R52.6: tab vista grafica · "week" | "month" | "area"
  // R55.0: apertura diretta al tab passato via query param (es. da Impostazioni → "Programma Pulizie")
  const [searchParams] = useSearchParams();
  const initialTab = ["week", "month", "area"].includes(searchParams.get("tab")) ? searchParams.get("tab") : "week";
  const [chartTab, setChartTab] = useState(initialTab);
  // R52.6: modal "Dettaglio giorno" (click su cella griglia/heatmap)
  const [dayDetail, setDayDetail] = useState(null); // { dateIso, items, expected }

  // Wizard
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardQueue, setWizardQueue] = useState([]);
  const [wizardIdx, setWizardIdx] = useState(0);
  const [form, setForm] = useState({});

  // R53.13: Quick edit per correggere errori
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const openEdit = (r) => setEditing({ ...r, esito: r.esito || (r.completata ? "eseguita" : "saltata") });

  const saveEdit = async (patched) => {
    // R80: form intero modificabile — deriva completata da esito + motivo
    const isExec = (patched.esito || "eseguita") === "eseguita";
    if (!isExec && !patched.motivo_salto) {
      toast.error("Seleziona il motivo del salto");
      throw new Error("motivo_salto mancante");
    }
    const validSkip = ["locale_chiuso", "area_inaccessibile"].includes(patched.motivo_salto);
    const body = {
      ...patched,
      esito: isExec ? "eseguita" : "saltata",
      motivo_salto: isExec ? null : patched.motivo_salto,
      completata: isExec || validSkip,
    };
    try {
      await api.put(`/pulizie/${patched.id}`, body);
      toast.success("Modifica salvata");
      await loadAll();
    } catch (e) {
      toast.error(apiErrMsg(e, "Errore modifica"));
      throw e;
    }
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [tpls, recs, todo, turniList] = await Promise.all([
        api.get("/pulizie-templates").catch(() => ({ data: [] })),
        api.get("/pulizie").catch(() => ({ data: [] })),
        api.get("/tasks/today").catch(() => ({ data: { pulizie: [] } })),
        fetchTurni(api),
      ]);
      setTemplates(tpls.data || []);
      setItems(recs.data || []);
      // tasks/today restituisce pulizie filtrate dal backend (con status)
      const pul = (todo.data?.pulizie || []).filter(t =>
        t.status === "due_today" || t.status === "overdue"
      );
      // R54.0 — usa la lista turni dinamica (1/2/3 turni) invece del hardcoded mattina/sera
      const currentSlot = findCurrentTurnoKey(turniList);
      const isDailyToday = (t) => {
        const f = t.frequenza || "giornaliera";
        if (f === "due_volte_giorno") return t.slot === currentSlot;
        return f === "giornaliera";
      };
      const isLaterToday = (t) =>
        (t.frequenza === "due_volte_giorno") && t.slot !== currentSlot;
      const today = pul.filter(isDailyToday);
      const laterToday = pul.filter(isLaterToday);
      const upcoming = pul.filter(t => !isDailyToday(t) && !isLaterToday(t));
      setTodoData({ tasks: pul, today, upcoming, laterToday });
    } catch {
      toast.error("Errore caricamento");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadAll(); }, []);

  const filteredItems = useMemo(() => {
    const startDate = startOfPeriod(periodo);
    const q = (search || "").toLowerCase().trim();
    return items.filter(r => {
      if (startDate && (r.eseguita_il || "") < startDate) return false;
      if (!q) return true;
      const hay = `${r.area || ""} ${r.attivita || ""} ${r.note || ""} ${r.eseguita_da || ""}`.toLowerCase();
      return hay.includes(q);
    }).sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  }, [items, periodo, search]);

  // ── R52.6: STATS per le 3 viste grafiche ──────────────────
  // Calcoliamo per ogni giorno: done (esecuzioni "ok") / expected (templates con freq che cade)
  // R53.15: restDays = giorni di chiusura settimanale dell'org (0=Lun..6=Dom).
  // Nei giorni di chiusura nessuna pulizia è dovuta → cella slate "chiuso".
  const restDays = useMemo(() => new Set((user?.giorni_riposo || []).filter(d => Number.isInteger(d) && d >= 0 && d <= 6)), [user?.giorni_riposo]);

  const stats = useMemo(() => {
    const itemsByDay = {}; // { "2026-02-23": [items...] }
    items.forEach(r => {
      // R53.15: eseguita_il è sempre "YYYY-MM-DD" string locale, no conversione necessaria
      const d = (r.eseguita_il || "").slice(0, 10);
      if (!d) return;
      (itemsByDay[d] = itemsByDay[d] || []).push(r);
    });

    // Helper: numero di template "attesi" per un dato giorno
    // R53.22 — fix bug Kira: i template pulizie hanno `frequenza` (stringa)
    // non `frequenza_giorni`. Senza mappa, le settimanali venivano conteggiate
    // come giornaliere → cella gialla anche con tutte le daily fatte.
    const FREQ_DAYS_PUL = { "due_volte_giorno": 0.5, "giornaliera": 1, "settimanale": 7, "mensile": 30, "annuale": 365 };
    const expectedPerDay = templates.reduce((acc, t) => {
      const freqStr = t.frequenza || "giornaliera";
      const freq = Math.max(0.5, Number(FREQ_DAYS_PUL[freqStr] ?? 1));
      return acc + (1 / freq);
    }, 0);

    const expectedRound = Math.round(expectedPerDay);

    const buildDayCell = (date) => {
      const iso = ymd(date);
      const dayItemsAll = itemsByDay[iso] || [];
      // R53.14: escludi esplicitamente i saltati (esito="saltata" o completata=false).
      const dayItems = dayItemsAll.filter(r => {
        if (r.esito === "saltata") return false;
        if (r.completata === false) return false;
        return true;
      });
      const done = dayItems.length;
      // R53.15: giorno di chiusura settimanale → cella "rest" slate (niente da fare).
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const dNorm = new Date(date); dNorm.setHours(0, 0, 0, 0);
      const isToday = dNorm.getTime() === today.getTime();
      const isFuture = dNorm.getTime() > today.getTime();
      const dowIt = (date.getDay() + 6) % 7;
      const isRest = restDays.has(dowIt);
      // R53.14: cap expected al massimo tra atteso e realmente fatto.
      const expected = Math.max(expectedRound, done);
      const pct = expected > 0 ? (done / expected) * 100 : 0;
      return { iso, date, done, expected, pct, isToday, isFuture, isRest, items: dayItemsAll };
    };

    // VISTA WEEK · settimana corrente (lun → dom)
    const wStart = startOfWeekIt();
    const week = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(wStart); d.setDate(wStart.getDate() + i);
      week.push(buildDayCell(d));
    }

    // VISTA MONTH · mese corrente (con padding inizio settimana)
    const mStart = startOfMonth();
    const mEnd = endOfMonth();
    const padBefore = dayOfWeekItIdx(mStart);
    const days = [];
    for (let i = 0; i < padBefore; i++) days.push(null);
    for (let d = new Date(mStart); d <= mEnd; d.setDate(d.getDate() + 1)) {
      days.push(buildDayCell(new Date(d)));
    }
    // pad fino a multiplo di 7
    while (days.length % 7 !== 0) days.push(null);

    // VISTA AREA · score per area negli ultimi 30 gg
    const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 30); cutoff.setHours(0, 0, 0, 0);
    const cutoffIso = ymd(cutoff);
    const byArea = {};
    templates.forEach(t => {
      const freqStr = t.frequenza || "giornaliera";
      const freq = Math.max(0.5, Number(FREQ_DAYS_PUL[freqStr] ?? 1));
      const exp = 30 / freq; // attese in 30gg per questo template
      if (!byArea[t.area]) byArea[t.area] = { area: t.area, expected: 0, done: 0 };
      byArea[t.area].expected += exp;
    });
    items.forEach(r => {
      const d = (r.eseguita_il || "").slice(0, 10);
      if (!d || d < cutoffIso) return;
      // R53.14: escludi saltati esplicitamente (allineato a buildDayCell)
      if (r.esito === "saltata" || r.completata === false) return;
      const key = r.area;
      if (!byArea[key]) byArea[key] = { area: key, expected: 0, done: 0 };
      byArea[key].done += 1;
    });
    const areas = Object.values(byArea).map(a => {
      // R53.14: cap expected al massimo tra atteso e realmente fatto, così se hai
      // pulito più del previsto la cella resta verde (non viene mai oltre il 100%).
      const expectedFinal = Math.max(Math.round(a.expected), a.done);
      return {
        ...a,
        expected: expectedFinal,
        pct: expectedFinal > 0 ? Math.min(100, Math.round((a.done / expectedFinal) * 100)) : 0,
      };
    }).sort((a, b) => a.pct - b.pct); // peggiori in cima

    return { week, monthDays: days, areas };
  }, [items, templates, restDays]);

  // Diario: raggruppa items per giorno (decrescente)
  const diary = useMemo(() => {
    const byDay = {};
    filteredItems.forEach(r => {
      const d = (r.eseguita_il || "").slice(0, 10);
      if (!d) return;
      (byDay[d] = byDay[d] || []).push(r);
    });
    return Object.entries(byDay)
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([dateIso, list]) => ({
        dateIso,
        items: list,
        // R53.14: escludi saltati e completata=false (coerente con buildDayCell)
        done: list.filter(r => r.esito !== "saltata" && r.completata !== false).length,
        total: list.length,
      }));
  }, [filteredItems]);

  // ── Wizard ────────────────────────────────────────────────
  const startWizard = () => {
    if (todoData.tasks.length === 0) { toast.info("Tutte le pulizie sono già fatte oggi"); return; }
    setWizardQueue(todoData.tasks);
    setWizardIdx(0);
    initWizardForm(todoData.tasks[0]);
    setWizardOpen(true);
  };

  const initWizardForm = (task) => {
    setForm({
      // R52.5.2: /tasks/today restituisce le pulizie con campo `template_id`
      // (NON `id`) — fix bug "field required" segnalato in produzione.
      template_id: task.template_id || task.id,
      area: task.area,
      attivita: task.attivita,
      frequenza: task.frequenza,
      esito: "eseguita",
      motivo_salto: null,
      note: "",
    });
  };

  const saveCurrent = async () => {
    if (form.esito === "saltata") {
      if (!form.motivo_salto) { toast.error("Scegli il motivo del salto"); return; }
      if (form.motivo_salto === "altro" && !(form.note || "").trim()) {
        toast.error("Per motivo 'altro' la nota è obbligatoria");
        return;
      }
    }
    try {
      const res = await safePost("/tasks/pulizia/complete", {
        template_id: form.template_id,
        note: form.note || "",
        esito: form.esito,
        motivo_salto: form.esito === "saltata" ? form.motivo_salto : null,
      }, { label: `Pulizia · ${form.area || ""} ${form.attivita || ""}` });
      if (res?.offline) {
        toast.success("Salvata offline · si sincronizzerà appena torna la rete");
      } else {
        toast.success(form.esito === "eseguita" ? "Pulizia registrata ✓" : "Salto registrato");
      }
      goNext();
    } catch (e) {
      toast.error(apiErrMsg(e, "Errore salvataggio"));
    }
  };

  const goNext = () => {
    const next = wizardIdx + 1;
    if (next < wizardQueue.length) {
      setWizardIdx(next);
      initWizardForm(wizardQueue[next]);
    } else {
      setWizardOpen(false);
      loadAll();
    }
  };

  const exportCsv = () => {
    const headers = ["Data", "Area", "Attività", "Frequenza", "Esito", "Motivo", "Operatore", "Nota"];
    const rows = filteredItems.map(r => [
      r.eseguita_il || "",
      r.area || "",
      r.attivita || "",
      r.frequenza || "",
      r.esito || (r.completata ? "eseguita" : "non eseguita"),
      r.motivo_salto || "",
      r.eseguita_da || "",
      (r.note || "").replace(/[\r\n]/g, " "),
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\r\n");
    const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `pulizie_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const currentTask = wizardQueue[wizardIdx];

  return (
    <div className="min-h-screen bg-slate-100 pb-24" data-testid="pulizie-ristorazione">
      <header className="bg-gradient-to-br from-[#13294B] to-[#1E3A5F] text-white px-5 pt-5 pb-4 rounded-b-2xl shadow-lg">
        <div className="max-w-md lg:max-w-3xl mx-auto flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-white flex items-center justify-center shadow-md shrink-0">
            <SprayCan className="w-6 h-6 text-[#13294B]" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">e-Log Ristorazione</p>
            <h1 className="text-xl font-bold leading-tight">Pulizie</h1>
          </div>
          {user?.role === "admin" && (
            <Link
              to="/setup-pulizie"
              className="w-10 h-10 rounded-full hover:bg-white/15 flex items-center justify-center transition"
              aria-label="Impostazioni programma pulizie"
              title="Impostazioni programma pulizie"
              data-testid="pulizie-settings-shortcut"
            >
              <Settings2 className="w-5 h-5 text-white/90" />
            </Link>
          )}
        </div>
      </header>

      <section className="max-w-md lg:max-w-3xl mx-auto px-4 -mt-3">
        <div className="bg-white rounded-2xl shadow-md p-4 border border-slate-200" data-testid="todo-card">
          {(() => {
            // R53.37 safety — todoData potrebbe arrivare da uno stato precedente
            // senza i campi today/upcoming/laterToday (es. setTodoData chiamato
            // altrove durante refactor). Calcolo defensive con fallback su tasks.
            const today = todoData.today ?? todoData.tasks ?? [];
            const upcoming = todoData.upcoming ?? [];
            const laterToday = todoData.laterToday ?? [];
            const total = todoData.tasks ?? [];
            return (
              <>
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
                    <SprayCan className="w-6 h-6 text-amber-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase tracking-widest text-amber-700 font-bold">Da fare oggi</p>
                    <p className="text-2xl font-extrabold text-slate-900 leading-tight" data-testid="todo-count">
                      {today.length} attività
                    </p>
                    <p className="text-[11px] text-slate-500 mt-0.5 truncate" data-testid="todo-subtitle">
                      {today.length === 0
                        ? "Pulizie del turno corrente completate ✓"
                        : today.slice(0, 3).map(t => t.slot_label ? `${t.area} (${t.slot_label})` : t.area).join(" · ")}
                    </p>
                    {laterToday.length > 0 && (
                      <p className="text-[11px] text-slate-600 mt-1.5" data-testid="todo-later">
                        Più tardi: {laterToday.length} {laterToday.length === 1 ? "pulizia" : "pulizie"} del turno {laterToday[0]?.slot_label || "successivo"}
                      </p>
                    )}
                    {upcoming.length > 0 && (
                      <p className="text-[11px] text-amber-700 mt-1.5 font-medium" data-testid="todo-upcoming">
                        Inoltre {upcoming.length} {upcoming.length === 1 ? "pulizia" : "pulizie"} in scadenza
                        ({upcoming.slice(0, 2).map(t => {
                          const f = t.frequenza === "settimanale" ? "sett." : t.frequenza === "mensile" ? "mens." : t.frequenza === "annuale" ? "ann." : "";
                          return f ? `${t.area} ${f}` : t.area;
                        }).join(" · ")})
                      </p>
                    )}
                  </div>
                </div>
                <button
                  onClick={startWizard}
                  disabled={total.length === 0}
                  className="mt-3 w-full bg-[#13294B] text-white rounded-xl py-3 text-sm font-bold flex items-center justify-center gap-2 active:scale-[.98] disabled:opacity-50"
                  data-testid="start-wizard-btn"
                >
                  <ArrowRight className="w-4 h-4" />
                  {total.length === 0 ? "Tutto fatto per oggi" : "Inizia pulizie"}
                </button>
              </>
            );
          })()}
        </div>
      </section>

      {/* R52.6: TAB CAROSELLO viste grafiche */}
      <section className="max-w-md lg:max-w-3xl mx-auto px-4 pt-5" data-testid="chart-tabs">
        <div className="bg-white rounded-xl p-1 border border-slate-200 flex gap-1">
          {[
            { v: "week",  l: "Settimana", I: LayoutGrid },
            { v: "month", l: "Mese",      I: BarChart3 },
            { v: "area",  l: "Aree",      I: Layers },
          ].map(t => {
            const Ico = t.I;
            return (
              <button
                key={t.v}
                onClick={() => setChartTab(t.v)}
                className={`flex-1 py-2 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 ${
                  chartTab === t.v ? "bg-[#13294B] text-white" : "text-slate-600"
                }`}
                data-testid={`tab-${t.v}`}
              ><Ico className="w-3.5 h-3.5" />{t.l}</button>
            );
          })}
        </div>
      </section>

      {/* R52.6: VISTA SETTIMANA */}
      {chartTab === "week" && (
        <section className="max-w-md lg:max-w-3xl mx-auto px-4 pt-3" data-testid="view-week">
          <div className="bg-white rounded-2xl border border-slate-200 p-3 shadow-sm">
            <p className="text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-2">Questa settimana</p>
            <div className="grid grid-cols-7 gap-1.5">
              {stats.week.map((c, i) => {
                const disabled = c.isFuture || c.isToday;
                const cls = c.isToday
                  ? "bg-blue-100 text-blue-700 ring-2 ring-blue-300"
                  : c.isFuture
                    ? "bg-slate-100 text-slate-400"
                    // R53.15: giorno chiusura settimanale → slate "chiuso"
                    : c.isRest && c.done === 0
                      ? "bg-slate-100 text-slate-500"
                      : colorClassFromPct(c.pct, c.expected > 0).cell;
                return (
                  <button
                    key={i}
                    onClick={() => !disabled && setDayDetail(c)}
                    disabled={disabled}
                    className={`aspect-square rounded-lg flex flex-col items-center justify-center font-bold text-[10px] ${cls} disabled:cursor-default`}
                    data-testid={`week-cell-${i}`}
                  >
                    <span>{DAY_LABELS[i]}</span>
                    <span className="text-[8px] opacity-90">
                      {c.isToday ? "oggi" : c.isFuture ? "—" : c.isRest && c.done === 0 ? "chiuso" : `${c.done}/${c.expected || "?"}`}
                    </span>
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] text-slate-400 mt-2 text-center italic">Tocca un giorno per i dettagli</p>
          </div>
        </section>
      )}

      {/* R52.6: VISTA MESE */}
      {chartTab === "month" && (
        <section className="max-w-md lg:max-w-3xl mx-auto px-4 pt-3" data-testid="view-month">
          <div className="bg-white rounded-2xl border border-slate-200 p-3 shadow-sm">
            <p className="text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-2">
              Heatmap · {MONTHS_IT[new Date().getMonth()]} {new Date().getFullYear()}
            </p>
            <div className="grid grid-cols-7 gap-1 text-center text-[9px] text-slate-400 font-semibold mb-1">
              {DAY_LABELS.map(l => <div key={l}>{l[0]}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {stats.monthDays.map((c, i) => {
                if (!c) return <div key={i} />;
                const disabled = c.isFuture || c.isToday;
                const cls = c.isToday
                  ? "bg-blue-100 text-blue-700 ring-2 ring-blue-300"
                  : c.isFuture
                    ? "bg-slate-100 text-slate-400"
                    // R53.15: chiusura settimanale → slate
                    : c.isRest && c.done === 0
                      ? "bg-slate-100 text-slate-400"
                      : colorClassFromPct(c.pct, c.expected > 0).cell;
                return (
                  <button
                    key={i}
                    onClick={() => !disabled && setDayDetail(c)}
                    disabled={disabled}
                    className={`aspect-square rounded text-[9px] font-semibold flex items-center justify-center ${cls}`}
                  >{c.date.getDate()}</button>
                );
              })}
            </div>
            <div className="flex items-center justify-end gap-2 mt-2 text-[9px] text-slate-500">
              <div className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded-sm bg-emerald-500"/>100%</div>
              <div className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded-sm bg-amber-500"/>50-99%</div>
              <div className="flex items-center gap-1"><div className="w-2.5 h-2.5 rounded-sm bg-rose-500"/>&lt;50%</div>
            </div>
          </div>
        </section>
      )}

      {/* R52.6: VISTA AREE */}
      {chartTab === "area" && (
        <section className="max-w-md lg:max-w-3xl mx-auto px-4 pt-3" data-testid="view-area">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <p className="text-[11px] uppercase tracking-widest font-bold text-slate-500 px-3 pt-3 pb-1">
              Performance per area · ultimi 30 gg
            </p>
            {stats.areas.length === 0 && (
              <div className="px-3 py-6 text-center text-sm text-slate-500">Nessun template configurato.</div>
            )}
            <div className="divide-y divide-slate-100">
              {stats.areas.map(a => {
                const c = colorClassFromPct(a.pct, a.expected > 0);
                return (
                  <div key={a.area} className="px-3 py-3 flex items-center gap-3" data-testid={`area-row-${a.area}`}>
                    <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
                      <SprayCan className="w-5 h-5 text-slate-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm text-slate-900 truncate">{a.area}</p>
                      <div className="mt-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full ${c.dot}`} style={{ width: `${Math.min(100, a.pct)}%` }} />
                      </div>
                      <p className="text-[10px] text-slate-500 mt-0.5">{a.done} su {a.expected} attesi</p>
                    </div>
                    <div className={`text-right ${c.text}`}>
                      <div className="font-extrabold text-xl">{a.pct}%</div>
                      <div className={`w-2.5 h-2.5 rounded-full inline-block ${c.dot}`}/>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <section className="max-w-md lg:max-w-3xl mx-auto px-4 pt-5 space-y-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:border-[#13294B]"
            placeholder="Cerca per area, operatore, nota…"
            data-testid="search-input"
          />
        </div>
        <div className="flex gap-1.5 overflow-x-auto">
          {PERIODI.map(p => (
            <button
              key={p.v}
              onClick={() => setPeriodo(p.v)}
              className={`px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap transition ${
                periodo === p.v ? "bg-[#13294B] text-white" : "bg-white text-slate-600 border border-slate-200"
              }`}
              data-testid={`chip-${p.v}`}
            >{p.l}</button>
          ))}
        </div>
      </section>

      {/* R52.6: DIARIO raggruppato per giorno (sostituisce lista card/tabella) */}
      <main className="max-w-md lg:max-w-3xl mx-auto px-4 py-4">
        <h2 className="text-[11px] uppercase tracking-widest font-bold text-slate-500 px-1 mb-2">
          Storico diario <span className="normal-case text-slate-400 font-normal">({filteredItems.length})</span>
        </h2>

        {loading && <div className="text-center text-slate-500 text-sm py-8">Caricamento…</div>}
        {!loading && diary.length === 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6 text-center text-sm text-slate-500" data-testid="empty-state">
            Nessuna pulizia registrata nel periodo selezionato.
          </div>
        )}

        {!loading && (
          <ul className="space-y-2" data-testid="diary-list">
            {diary.map(day => {
              const expanded = expandedId === day.dateIso;
              const pct = day.total > 0 ? (day.done / day.total) * 100 : 0;
              const c = colorClassFromPct(pct, day.total > 0);
              return (
                <li key={day.dateIso} className="bg-white rounded-2xl border border-slate-200 shadow-sm" data-testid={`diary-day-${day.dateIso}`}>
                  <button onClick={() => setExpandedId(expanded ? null : day.dateIso)} className="w-full flex items-center justify-between p-3 text-left">
                    <div>
                      <p className="font-bold text-sm">📅 {fmtDate(day.dateIso)}</p>
                      <p className="text-[11px] text-slate-500">{day.dateIso}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className={`w-2.5 h-2.5 rounded-full ${c.dot}`} />
                      <span className={`font-bold text-sm ${c.text}`}>{day.done}/{day.total}</span>
                      {expanded ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
                    </div>
                  </button>
                  {expanded && (
                    <div className="px-3 pb-3 pt-1 border-t border-slate-100 space-y-1.5">
                      {day.items.map(r => {
                        const isSkipped = (r.esito === "saltata") || (r.completata === false);
                        const validSkip = MOTIVI.find(m => m.v === r.motivo_salto)?.valid;
                        const motivoLabel = MOTIVI.find(m => m.v === r.motivo_salto)?.l;
                        const canEdit = user?.role !== "operator" || r.eseguita_da === user?.name;
                        return (
                          <div key={r.id} className="flex items-center gap-2 text-[12px]">
                            <button onClick={() => setDetail(r)} className="flex items-center gap-2 flex-1 min-w-0 text-left active:opacity-70" data-testid={`detail-${r.id}`}>
                              <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                                isSkipped ? (validSkip ? "bg-slate-400" : "bg-rose-500") : "bg-emerald-500"
                              }`} />
                              <span className="text-slate-700 flex-1 truncate">{r.area} · {r.attivita}</span>
                              <span className="text-slate-400 text-[11px] shrink-0">
                                {(r.created_at || "").slice(11, 16)} · {r.eseguita_da || "—"}
                              </span>
                              {isSkipped && motivoLabel && (
                                <span className={`text-[10px] shrink-0 ${validSkip ? "text-slate-500" : "text-rose-600 font-semibold"}`}>{motivoLabel}</span>
                              )}
                            </button>
                            {canEdit && (
                              <button onClick={(e) => { e.stopPropagation(); openEdit(r); }}
                                      className="p-1 rounded hover:bg-blue-50 text-blue-700 shrink-0"
                                      data-testid={`edit-${r.id}`} title="Modifica">
                                <Pencil className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {filteredItems.length > 0 && (
          <button
            onClick={exportCsv}
            className="mt-5 w-full py-3 rounded-2xl border border-slate-300 bg-white text-slate-800 font-semibold text-sm flex items-center justify-center gap-2"
            data-testid="export-csv-btn"
          >
            <Download className="w-4 h-4" /> Esporta CSV ({filteredItems.length} pulizie)
          </button>
        )}
      </main>

      {/* R52.6: MODAL DETTAGLIO GIORNO (click su cella griglia/heatmap) */}
      <Dialog open={!!dayDetail} onOpenChange={(o) => { if (!o) setDayDetail(null); }}>
        <DialogContent className="max-w-sm p-0 overflow-hidden gap-0 [&>button.absolute]:hidden" data-testid="day-detail-dialog">
          <DialogTitle className="sr-only">Dettaglio giorno</DialogTitle>
          <DialogDescription className="sr-only">Pulizie registrate in questa giornata.</DialogDescription>
          {dayDetail && (
            <>
              <div className="bg-gradient-to-br from-[#13294B] to-[#1E3A5F] text-white px-4 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">
                    {dayDetail.date.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                  </p>
                  <h3 className="font-bold text-base">{dayDetail.done} su {dayDetail.expected || "?"} completate</h3>
                </div>
                <button onClick={() => setDayDetail(null)} className="shrink-0 w-10 h-10 rounded-full hover:bg-white/15 flex items-center justify-center" aria-label="Chiudi" data-testid="day-detail-close">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-4 space-y-2 max-h-[60vh] overflow-y-auto">
                {dayDetail.items.length === 0 ? (
                  <p className="text-center text-sm text-slate-500 py-6">Nessuna pulizia registrata in questo giorno.</p>
                ) : dayDetail.items.map(r => {
                  const isSkipped = (r.esito === "saltata") || (r.completata === false);
                  const validSkip = MOTIVI.find(m => m.v === r.motivo_salto)?.valid;
                  const motivoLabel = MOTIVI.find(m => m.v === r.motivo_salto)?.l;
                  return (
                    <div key={r.id} className={`flex items-center gap-3 p-2 rounded-xl ${
                      isSkipped ? (validSkip ? "bg-slate-50" : "bg-rose-50") : "bg-emerald-50"
                    }`}>
                      <div className={`w-2.5 h-2.5 rounded-full ${
                        isSkipped ? (validSkip ? "bg-slate-400" : "bg-rose-500") : "bg-emerald-500"
                      }`} />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm">{r.area}</p>
                        <p className="text-[10px] text-slate-500">
                          {(r.created_at || "").slice(11, 16)} · {r.eseguita_da || "—"}
                          {isSkipped && motivoLabel && ` · ${motivoLabel}`}
                        </p>
                      </div>
                      <span className={`text-xs font-bold ${
                        isSkipped ? (validSkip ? "text-slate-500" : "text-rose-600") : "text-emerald-600"
                      }`}>
                        {isSkipped ? (validSkip ? "OK" : "⚠ NC") : "✓"}
                      </span>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* WIZARD MODAL */}
      <Dialog open={wizardOpen} onOpenChange={setWizardOpen}>
        <DialogContent
          className="max-w-sm p-0 overflow-hidden gap-0 [&>button.absolute]:hidden"
          data-testid="wizard-dialog"
        >
          <DialogTitle className="sr-only">Pulizia · {currentTask?.area || ""}</DialogTitle>
          <DialogDescription className="sr-only">
            Registra l'esecuzione della pulizia per l'area {currentTask?.area || ""}. Puoi segnarla come eseguita o saltata con motivo.
          </DialogDescription>
          {currentTask && (
            <>
              <div className="bg-gradient-to-br from-[#13294B] to-[#1E3A5F] text-white px-4 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">
                    Area {wizardIdx + 1} di {wizardQueue.length}
                  </p>
                  <h3 className="font-bold text-base truncate">{currentTask.area}</h3>
                  <p className="text-[11px] text-slate-300 truncate">{currentTask.attivita}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setWizardOpen(false)}
                  className="shrink-0 w-10 h-10 rounded-full hover:bg-white/15 active:bg-white/25 flex items-center justify-center text-white/90"
                  aria-label="Chiudi"
                  data-testid="wiz-close"
                ><X className="w-5 h-5" /></button>
              </div>

              <div className="p-4 space-y-3">
                <div className="bg-slate-100 rounded-xl px-3 py-2 text-[11px] text-slate-600">
                  Frequenza: <b className="text-slate-900">{currentTask.frequenza}</b>
                  {user?.name && <> · Operatore: <b className="text-slate-900">{user.name}</b></>}
                </div>

                <div>
                  <label className="text-xs font-semibold text-slate-700">Esito</label>
                  <div className="mt-1 grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, esito: "eseguita", motivo_salto: null })}
                      className={`rounded-xl py-3 text-sm font-bold flex items-center justify-center gap-2 transition border ${
                        form.esito === "eseguita"
                          ? "bg-emerald-500 text-white border-emerald-500 shadow"
                          : "bg-white text-slate-700 border-slate-300"
                      }`}
                      data-testid="wiz-eseguita"
                    >
                      <CheckCircle2 className="w-5 h-5" /> Eseguita
                    </button>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, esito: "saltata" })}
                      className={`rounded-xl py-3 text-sm font-bold flex items-center justify-center gap-2 transition border ${
                        form.esito === "saltata"
                          ? "bg-rose-500 text-white border-rose-500 shadow"
                          : "bg-white text-slate-700 border-slate-300"
                      }`}
                      data-testid="wiz-saltata"
                    >
                      <XCircle className="w-5 h-5" /> Saltata
                    </button>
                  </div>
                </div>

                {form.esito === "saltata" && (
                  <div data-testid="wiz-motivo-group">
                    <label className="text-xs font-semibold text-slate-700">Motivo del salto <span className="text-rose-600">*</span></label>
                    <div className="mt-1 space-y-1.5">
                      {MOTIVI.map(m => (
                        <label key={m.v} className={`flex items-center gap-2 px-3 py-2 rounded-xl border cursor-pointer ${form.motivo_salto === m.v ? "bg-slate-100 border-[#13294B]" : "bg-white border-slate-200"}`} data-testid={`wiz-motivo-${m.v}`}>
                          <input
                            type="radio"
                            name="motivo"
                            value={m.v}
                            checked={form.motivo_salto === m.v}
                            onChange={() => setForm({ ...form, motivo_salto: m.v })}
                          />
                          <span className="text-sm flex-1">{m.l}</span>
                          {m.valid && <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full">no NC</span>}
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <label className="text-xs font-semibold text-slate-700">
                    Nota {form.esito === "saltata" && form.motivo_salto === "altro" && <span className="text-rose-600">(obbligatoria)</span>}
                  </label>
                  <textarea
                    value={form.note || ""}
                    onChange={(e) => setForm({ ...form, note: e.target.value })}
                    placeholder="Es. prodotti finiti / specifica…"
                    className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg text-sm h-16 resize-none"
                    data-testid="wiz-note"
                  />
                </div>
              </div>

              <div className="p-3 grid grid-cols-2 gap-2 border-t border-slate-200">
                <button onClick={goNext} className="rounded-xl border border-slate-300 py-2.5 text-xs font-semibold" data-testid="wiz-skip">
                  Salta
                </button>
                <button onClick={saveCurrent} className="rounded-xl bg-[#13294B] text-white py-2.5 text-xs font-bold" data-testid="wiz-save">
                  {wizardIdx + 1 < wizardQueue.length ? "Salva e prossima" : "Salva e chiudi"}
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* R53.13: Quick edit per correggere errori di battitura */}
      <QuickEditDialog
        open={!!editing}
        onClose={() => setEditing(null)}
        record={editing}
        title={`Modifica pulizia · ${editing?.area || ""}`}
        fields={[
          { key: "esito", label: "Esito", type: "select", options: [{ v: "eseguita", l: "✅ Eseguita" }, { v: "saltata", l: "⏭ Saltata" }] },
          { key: "motivo_salto", label: "Motivo del salto", type: "select", options: MOTIVI, placeholder: "— scegli il motivo —", showIf: (d) => d.esito === "saltata", helper: "Locale chiuso / area inaccessibile non generano Non Conformità." },
          { key: "eseguita_da", label: "Eseguita da", type: "text" },
          { key: "eseguita_il", label: "Data esecuzione", type: "date" },
          { key: "note", label: "Note", type: "textarea" },
        ]}
        onSave={saveEdit}
        onDelete={["admin", "superadmin"].includes(user?.role) ? async (rec) => {
          try {
            await api.delete(`/pulizie/${rec.id}`);
            toast.success("Registrazione eliminata");
            await loadAll();
          } catch (e) { toast.error(apiErrMsg(e, "Errore eliminazione")); throw e; }
        } : undefined}
      />

      {/* Scheda dettaglio pulizia (read-only) */}
      <RegistroDetailDialog
        open={!!detail}
        onClose={() => setDetail(null)}
        statusLabel={detail ? (((detail.esito === "saltata") || (detail.completata === false)) ? "Saltata" : "Completata") : ""}
        statusTone={detail ? (((detail.esito === "saltata") || (detail.completata === false))
          ? (MOTIVI.find(m => m.v === detail.motivo_salto)?.valid ? "neutral" : "bad")
          : "ok") : "neutral"}
        title={detail ? `${detail.area} · ${detail.attivita}` : ""}
        subtitle={detail ? `${(detail.eseguita_il || detail.created_at || "").slice(0, 10)} · ore ${(detail.created_at || "").slice(11, 16)}` : ""}
        cells={detail ? [
          { label: "Eseguita da", value: detail.eseguita_da || "—" },
          { label: "Frequenza", value: ({ due_volte_giorno: "2 volte/giorno", giornaliera: "Giornaliera", settimanale: "Settimanale", mensile: "Mensile", annuale: "Annuale" })[detail.frequenza] || detail.frequenza || "—" },
          { label: "Esito", value: ((detail.esito === "saltata") || (detail.completata === false)) ? "Saltata" : "Completata", tone: ((detail.esito === "saltata") || (detail.completata === false)) ? "warn" : undefined },
          ((detail.esito === "saltata") || (detail.completata === false)) ? { label: "Motivo", value: MOTIVI.find(m => m.v === detail.motivo_salto)?.l || detail.motivo_salto || "—" } : null,
        ].filter(Boolean) : []}
        note={detail?.note}
        onEdit={detail && (user?.role !== "operator" || detail.eseguita_da === user?.name) ? () => { openEdit(detail); setDetail(null); } : null}
      />
    </div>
  );
}
